import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { enumEntityType } from '@app/data-layer/enums/isf';
import { AddressModel, ShipmentModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { IsfModel } from '@app/data-layer/models/isf';
import { ShipmentsV2Repository } from '@app/data-layer/repositories';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { GenericService } from '@services/generic.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IsfOcrQuickFixService, IsfPossibleEntitiesService, IsfStateService } from '@shared/isf-mutual/services';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { merge, of, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-isf-ocr-quick-fix-ship-to-details',
  templateUrl: './isf-ocr-quick-fix-ship-to-details.component.html',
  styleUrls: ['./isf-ocr-quick-fix-ship-to-details.component.scss'],
})
export class IsfOcrQuickFixShipToDetailsComponent {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public shipToFields: ISectionFormField[];

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public isf: IsfModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public shipToDisplayFields: ISectionFormField[] = [];
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumEntityType: typeof enumEntityType = enumEntityType;
  public isEditMode: boolean = false;
  public isDesktop: boolean = false;
  public possibleIsfEntities: EntityModel[] = [];
  public importer: EntityModel;
  public consignee: EntityModel;
  public buyer: EntityModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private cdr: ChangeDetectorRef,
    private genericService: GenericService,
    private shipmentsV2Repository: ShipmentsV2Repository,
    private isfOcrQuickFixService: IsfOcrQuickFixService,
    private isfPossibleEntitiesService: IsfPossibleEntitiesService,
    private isfStateService: IsfStateService,
  ) {}

  ngOnInit(): void {
    const isf = this.isfOcrQuickFixService.getIsf();
    this.syncIsf(isf);

    this.initializeFormGroup();

    this.isEditMode = this.isAnyFormBlockFieldEmpty(this.shipToFields, this.formGroup);

    this.fh.updateEntityValidation(true, this.formGroup);

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    this.setFieldsProperties();

    this.populateDisplayErrors(this.formGroup);
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || !fg.get(field.name)?.value || isFgInvalid);
    const shipToAddressErrors = this.isf?.shipment?.shipTo?.address?.validationMessages || {};

    return !!invalidFields || !UtilsHelper.isObjectEmpty(shipToAddressErrors);
  }

  public onBlockEdit(isShowEditBlock: boolean) {
    this.isEditMode = isShowEditBlock;
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    if (isNeedCheck && this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      if (Object.keys(invalidFields).length) {
        this.fh.markAsTouched(this.formGroup);
        this.openMissingFieldsModal(invalidFields);
        return;
      }
    }

    this.updateOrCreateShipTo()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isf) => {
        this.isfOcrQuickFixService.setIsf(isf);
        this.isfStateService.updateCurrentIsf(isf);
        this.dataSaved.emit();
      });
  }

  public onClearForm(): void {
    this.formGroup.reset();
  }

  public getEntityOverrideLabel(entity: EntityModel): string {
    return `INFO.ST.USE_${entity.entityCode}_DESKTOP`;
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private setFieldsProperties() {
    const shipTo = this.isf?.shipment?.shipTo;

    const addressFieldMissingPartsErrorText = this.addressFieldMissingPartsErrorText();
    const shipToAddressDisplayLine = !!addressFieldMissingPartsErrorText ? '' : this.getAddressLines(shipTo?.address);
    const nameField = this.shipToFields.find((field) => field.name === 'name');

    this.shipToDisplayFields.push(
      {
        ...nameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: shipTo?.name,
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: shipToAddressDisplayLine,
      },
    );

    this.cdr.markForCheck();
  }

  private populateDisplayErrors(fg: UntypedFormGroup) {
    Object.keys(fg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, fg));
          break;
        case 'address':
          this.displayErrors.set(fieldName, this.addressFieldMissingPartsErrorText());
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = this.fh.createErrorsForIsfFormGroup();
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private addressFieldMissingPartsErrorText(): string {
    const shipToAddressErrors = this.isf?.shipment?.shipTo?.address?.validationMessages || {};
    const missingAddressPartsText = !UtilsHelper.isObjectEmpty(shipToAddressErrors) ? 'BACK_ERROR.MISSING_ADDRESS_PARTS' : '';
    return missingAddressPartsText;
  }

  private getAddressLines(data: AddressModel): string {
    if (!data) {
      return '';
    }

    const state = data.state ? data.state.description : null;
    const country = data.country ? data.country.description : null;
    const address = [data?.line1, data?.line2, data?.line3]
      .filter((part) => !!part)
      .join('\n')
      .trim();

    const line1 = [address, data.city]
      .filter((part) => !!part)
      .join(', ')
      .trim();
    const line2 = [country, state]
      .filter((part) => !!part)
      .join(', ')
      .trim();

    return line1 + line2;
  }

  private updateOrCreateShipTo() {
    const shipTo: EntityModel = this.formGroup.value;
    const shipToId = this.formGroup.get('id').value;

    let newShipmentData: Partial<ShipmentModel>;

    if (shipToId === this.importer?.id) {
      newShipmentData = {
        ...this.isf.shipment,
        shipTo: this.importer,
        shipToId: this.importer?.id,
      };
    } else if (shipToId === this.consignee?.id) {
      newShipmentData = {
        ...this.isf.shipment,
        shipTo: this.consignee,
        shipToId: this.consignee?.id,
      };
    } else if (shipToId === this.buyer?.id) {
      newShipmentData = {
        ...this.isf.shipment,
        shipTo: this.buyer,
        shipToId: this.buyer?.id,
      };
    } else {
      newShipmentData = {
        ...this.isf.shipment,
        shipTo,
        shipToId,
      };
    }

    return of(shipTo).pipe(
      switchMap((entity) => {
        if (!shipToId) {
          entity.addressId = 0;
          entity.address.id = 0;
          entity.entityCode = enumEntityModelCode.SHIP_TO;
          return this.isfStateService.createEntity(entity);
        } else {
          return this.isfStateService.updateEntity(entity);
        }
      }),
      switchMap((entity) => {
        return this.updateShipment({
          ...newShipmentData,
          shipTo: entity,
          shipToId: entity.id,
        });
      }),
      switchMap((newShipment) => this.isfStateService.updateShipment(newShipment)),
      switchMap((shipment) => {
        return this.isfStateService.updateIsf({
          ...this.isf,
          shipment,
        });
      }),
      switchMap((isf) => this.isfStateService.loadIsf(isf.referenceNumber)),
      tap((isf) => {
        this.syncIsf(isf);
      }),
    );
  }

  private updateShipment(newShipmentData?: Partial<ShipmentModel>) {
    if (!newShipmentData) {
      newShipmentData = { ...this.isf?.shipment };
    }

    return this.shipmentsV2Repository.update(this.isf?.shipment?.id, newShipmentData);
  }

  private initializeFormGroup() {
    this.formGroup = this.fh.createEntityModelFormGroup(enumEntityModelCode.SHIP_TO);

    this.formGroup.patchValue(this.isf?.shipment?.shipTo);

    this.formGroup
      .get('id')
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged(), startWith(this.formGroup.get('id').value))
      .subscribe((shipToId) => {
        this.applyUsedEntityOrShipTo(shipToId);
      });

    merge(this.formGroup.get('name').valueChanges, this.formGroup.get('address').valueChanges)
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((_) => {
        const isUsedEntityResetNeeded =
          !this.formGroup.pristine &&
          ((this.importer && this.formGroup.get('id').value === this.importer.id) ||
            (this.consignee && this.formGroup.get('id').value === this.consignee.id) ||
            (this.buyer && this.formGroup.get('id').value === this.buyer.id));

        if (isUsedEntityResetNeeded) {
          this.formGroup.get('id').patchValue(this.isf?.shipment?.shipTo?.id || 0);
        }
      });

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private applyUsedEntityOrShipTo(id: number) {
    if (this.importer && id === this.importer.id) {
      this.formGroup.markAsPristine();
      this.fh.patchIsfEntity(this.formGroup, this.importer);
    } else if (this.consignee && id === this.consignee.id) {
      this.formGroup.markAsPristine();
      this.fh.patchIsfEntity(this.formGroup, this.consignee);
    } else if (this.buyer && id === this.buyer.id) {
      this.formGroup.markAsPristine();
      this.fh.patchIsfEntity(this.formGroup, this.buyer);
    } else {
      this.fh.patchIsfEntity(this.formGroup, this.isf?.shipment?.shipTo);
    }
  }

  private syncIsf(isf: IsfModel) {
    this.isf = isf;
    this.importer = this.isf?.importer;
    this.consignee = this.isf?.shipment?.consignee;
    this.buyer = this.isf?.shipment?.buyer;
    this.possibleIsfEntities = this.isfPossibleEntitiesService.calculatePossibleIsfEntities(
      enumEntityModelCode.SHIP_TO,
      this.possibleIsfEntities,
    );
  }
}
